import { useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { validateImageFile, generateUniqueFileName, resizeImage, getImageUrl } from "@/lib/imageUtils";

interface LogoUploadProps {
  empresaId: string;
  currentLogoUrl?: string | null;
  onLogoUpdated: (newLogoUrl: string | null) => void;
}

export function LogoUpload({ empresaId, currentLogoUrl, onLogoUpdated }: LogoUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentLogoUrl || null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    try {
      setUploading(true);

      // Delete old logo if exists
      if (currentLogoUrl) {
        const oldPath = currentLogoUrl.split('/').pop();
        if (oldPath) {
          await supabase.storage.from('empresa-logos').remove([oldPath]);
        }
      }

      // Resize and optimize image
      const optimizedBlob = await resizeImage(file, 500, 500);
      const fileName = generateUniqueFileName(file, empresaId);

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('empresa-logos')
        .upload(fileName, optimizedBlob, {
          contentType: 'image/webp',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const logoUrl = getImageUrl(fileName);

      // Update empresa logo_url in database
      const { error: updateError } = await supabase
        .from('empresas')
        .update({ logo_url: fileName })
        .eq('id', empresaId);

      if (updateError) throw updateError;

      setPreview(logoUrl);
      onLogoUpdated(logoUrl);
      toast.success('Logo actualizado correctamente');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Error al subir el logo');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      setUploading(true);

      // Delete from storage
      if (currentLogoUrl) {
        const oldPath = currentLogoUrl.split('/').pop();
        if (oldPath) {
          await supabase.storage.from('empresa-logos').remove([oldPath]);
        }
      }

      // Update database
      const { error } = await supabase
        .from('empresas')
        .update({ logo_url: null })
        .eq('id', empresaId);

      if (error) throw error;

      setPreview(null);
      onLogoUpdated(null);
      toast.success('Logo eliminado correctamente');
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error('Error al eliminar el logo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium">Logo de la Empresa</label>
      
      <div className="flex items-center gap-4">
        {preview ? (
          <div className="relative w-32 h-20 rounded-lg border-2 border-border overflow-hidden bg-card p-2">
            <img
              src={preview}
              alt="Logo preview"
              className="w-full h-full object-contain"
            />
            {!uploading && (
              <button
                onClick={handleRemoveLogo}
                className="absolute top-1 right-1 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ) : (
          <div className="w-24 h-24 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-muted/30">
            <Upload className="h-8 w-8 text-muted-foreground" />
          </div>
        )}

        <div className="flex-1">
          <input
            type="file"
            id={`logo-upload-${empresaId}`}
            accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
            onChange={handleFileSelect}
            disabled={uploading}
            className="hidden"
          />
          <label htmlFor={`logo-upload-${empresaId}`}>
            <Button
              type="button"
              variant="outline"
              disabled={uploading}
              asChild
            >
              <span className="cursor-pointer">
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Subiendo...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    {preview ? 'Cambiar Logo' : 'Subir Logo'}
                  </>
                )}
              </span>
            </Button>
          </label>
          <p className="text-xs text-muted-foreground mt-2">
            PNG, JPG, SVG o WebP. Máximo 2MB.
          </p>
        </div>
      </div>
    </div>
  );
}
